// src/crm/conversations/ConversationThread.tsx
import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useConversation } from './useConversation'
import { useSendMessage } from './useSendMessage'
import { useAddManualMessage } from './useAddManualMessage'
import { MessageBubble } from './MessageBubble'
import { EmailComposer } from './EmailComposer'
import { ManualExchangeForm } from './ManualExchangeForm'
import { ApiClientError } from '../../lib/api'
import type { Creator, Deal } from '../../lib/types'

type Action = 'email' | 'manual' | null

type Props = {
  creatorId: string
  creatorEmail: string | null
  contact: Creator
  deal: Deal | null
  appName: string
}

export function ConversationThread({ creatorId, creatorEmail, contact, deal, appName }: Props) {
  const qc = useQueryClient()
  const { data, isLoading, isError } = useConversation(creatorId)
  const sendMessage = useSendMessage(creatorId, qc)
  const addManual = useAddManualMessage(creatorId, qc)
  const [activeAction, setActiveAction] = useState<Action>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // Scroll bas à chaque nouveau message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data?.messages.length])

  // Reset erreur à chaque ouverture du composer
  useEffect(() => {
    if (activeAction !== 'email') setSendError(null)
  }, [activeAction])

  if (isLoading) {
    return (
      <div style={{ padding: '1.5rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Chargement du fil…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div style={{ padding: '1.5rem', color: 'var(--arancio)', fontSize: '0.875rem' }}>
        Impossible de charger les échanges.
      </div>
    )
  }

  const { messages } = data

  return (
    <div data-testid="conversation-thread" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Liste messages */}
      {messages.length === 0 ? (
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--dim)',
            fontSize: '0.875rem',
          }}
        >
          Aucun échange pour ce créateur. Commencez par envoyer un email ou noter un échange.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            maxHeight: '480px',
            overflowY: 'auto',
            paddingRight: '0.25rem',
          }}
        >
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Barre d'actions */}
      {activeAction === null && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setActiveAction('email')}
            style={{
              padding: '0.4375rem 0.875rem',
              background: 'var(--elevated)',
              border: '1px solid var(--line-strong)',
              borderRadius: '6px',
              color: 'var(--fg)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            ✉ Rédiger un email
          </button>
          <button
            type="button"
            onClick={() => setActiveAction('manual')}
            style={{
              padding: '0.4375rem 0.875rem',
              background: 'transparent',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              color: 'var(--muted)',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Ajouter un échange
          </button>
        </div>
      )}

      {/* Composer email */}
      {activeAction === 'email' && (
        <EmailComposer
          creatorId={creatorId}
          creatorEmail={creatorEmail}
          contact={contact}
          deal={deal}
          appName={appName}
          isSending={sendMessage.isPending}
          sendError={sendError}
          onCancel={() => setActiveAction(null)}
          onSend={(input) => {
            setSendError(null)
            sendMessage.mutate(input, {
              onSuccess: () => setActiveAction(null),
              onError: (err) => {
                if (err instanceof ApiClientError) {
                  setSendError(err.message)
                } else {
                  setSendError("Erreur lors de l'envoi.")
                }
              },
            })
          }}
        />
      )}

      {/* Formulaire échange manuel */}
      {activeAction === 'manual' && (
        <ManualExchangeForm
          creatorId={creatorId}
          contact={contact}
          deal={deal}
          appName={appName}
          isAdding={addManual.isPending}
          onCancel={() => setActiveAction(null)}
          onAdd={(input) => {
            addManual.mutate(input, {
              onSuccess: () => setActiveAction(null),
            })
          }}
        />
      )}
    </div>
  )
}
